import React, {FC, useEffect, useState} from "react";
import cls from "./customInnerPage.module.scss";
import Slider from "react-slick";
import NewPhotoSwipe from "./NewPhotoSwipe";
import {CarouselNext, CarouselPrev, ZoomIn24Svg} from "../../../svg";
import Image from "next/image";
import {domainUrl} from "../../../helper";

interface IProps {
    images: Array<string>,
    dbName: string
}

const CustomProductGallery: FC<IProps> = (
    {
        images,
        dbName
    }
) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [nav1, setNav1] = useState<Slider | null>(null);
    const [nav2, setNav2] = useState<Slider | null>(null);

    const showArrows = (slidesToShow: number) => images.length > slidesToShow;

    const settings = {
        slidesToShow: 4,
        arrows: showArrows(4),
        infinite: images.length > 4,
        centerMode: true,
        centerPadding: '0',
        swipeToSlide: true,
        focusOnSelect: true,
        nextArrow: <CarouselNext/>,
        prevArrow: <CarouselPrev/>,
        responsive: [
            {
                breakpoint: 1170,
                settings: {
                    slidesToShow: 3,
                    infinite: images.length > 3,
                    arrows: showArrows(3),
                },
            },
            {
                breakpoint: 991,
                settings: {
                    slidesToShow: 2,
                    infinite: images.length > 2,
                    arrows: showArrows(2),
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 4,
                    infinite: images.length > 4,
                    arrows: showArrows(4),
                },
            },
            {
                breakpoint: 525,
                settings: {
                    slidesToShow: 3,
                    infinite: images.length > 3,
                    arrows: showArrows(3),
                },
            },
            {
                breakpoint: 425,
                settings: {
                    slidesToShow: 2,
                    infinite: images.length > 2,
                    arrows: showArrows(2),
                },
            }
        ],
    };

    useEffect(() => {
        if (isOpen) {
            const initializeGallery = async () => {
                const options = {
                    index: currentIndex,
                    bgOpacity: 0.9,
                    history: false
                };

                const items = images.map((image) => ({
                    src: domainUrl(`${dbName}/${image}`),
                    msrc: domainUrl(`${dbName}/${image}`),
                    w: 1000,
                    h: 1000,
                }));
                const { default: PhotoSwipe } = await import("photoswipe");
                const { default: PhotoSwipeUI_Default } = await import("photoswipe/dist/photoswipe-ui-default");

                const gallery = new PhotoSwipe(document.querySelector('.pswp') as HTMLElement, PhotoSwipeUI_Default, items, options);
                gallery.init();

                gallery.listen('beforeChange', () => {
                    const index = gallery.getCurrentIndex();
                    nav1?.slickGoTo(index);
                    setCurrentIndex(index);
                });

                gallery.listen('close', () => {
                    setIsOpen(false);
                });

                return () => {
                    gallery.destroy();
                };
            }
            initializeGallery().then(r => r)
        }
    }, [isOpen]);

    const openGallery = (index: number) => {
        setIsOpen(true);
        setCurrentIndex(index);
    };

    return (
        <div className={`${cls.product_gallery} ${isOpen ? cls.product_gallery_priority : ""}`}>
            <div className={cls["product-gallery__featured"]}>
                <NewPhotoSwipe/>
                <Slider
                    asNavFor={nav2 as Slider}
                    ref={slider => setNav1(slider)}
                    arrows={false}
                >
                    {images.map((image, index) => (
                        <div className={cls.image_wrapper} onClick={() => openGallery(index)} key={index}>
                            <Image
                                src={domainUrl(`${dbName}/${image}`)}
                                alt="product image"
                                layout="fill"
                                objectFit='contain'
                                priority
                                loading="eager"
                                sizes="(max-width: 425px) 385px, (max-width: 768px) 730px, (max-width: 1200px) 555px, 585px"
                            />
                            <button
                                type="button"
                                aria-label="image zoom"
                                className={cls["product-gallery__zoom"]}
                                onClick={() => openGallery(index)}
                            >
                                <ZoomIn24Svg/>
                            </button>
                        </div>
                    ))}
                </Slider>
            </div>
            {images.length > 1 ? (
                <div className={cls["product-gallery__carousel"]}>
                    <Slider
                        asNavFor={nav1 as Slider}
                        ref={slider => setNav2(slider)}
                        {...settings}
                    >
                        {images.map((image, index) => (
                            <div className={cls.image_wrapper} key={index}>
                                <Image
                                    src={domainUrl(`${dbName}/${image}`)}
                                    alt="product image"
                                    layout="fill"
                                    objectFit='contain'
                                    loading="lazy"
                                    sizes="60px"
                                />
                            </div>
                        ))}
                    </Slider>
                </div>
            ) : null}
        </div>
    )
};

export default CustomProductGallery;